import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import DeleteModal from "../../components/admin/DeleteModal";
import { useNavigate } from "react-router-dom";
import SearchBar from "../ui/search-bar";
import { fetchProjects, removeProject } from "../../hooks/useProject";
import DOMPurify from "dompurify";

const ProjectsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    project: null,
  });
  const [projectList, setProjectList] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch projects from API
  const getProjects = async () => {
    try {
      const res = await fetchProjects();
      if (res.success) setProjectList(res.projects);
      // console.log(res)
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to fetch projects",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    getProjects();
  }, []);

  const filteredProjects = projectList?.filter(
    (project) =>
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Delete project handler
  const handleDelete = async () => {
    try {
      if (!deleteModal.project) return;

      await removeProject(deleteModal.project._id);

      setProjectList((prev) =>
        prev?.filter((p) => p._id !== deleteModal.project._id)
      );

      setDeleteModal({ isOpen: false, project: null });

      toast({
        title: "Project Deleted!",
        description: `${deleteModal.project.title} has been removed successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Projects Management</h2>
          <p className="text-muted-foreground">
            Manage your portfolio projects
          </p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-700"
          onClick={() => navigate("/admin/projects/add")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Project
        </Button>
      </div>

      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredProjects?.map((project, index) => (
            <motion.div
              key={project._id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <Card className="hover:shadow-lg transition-all duration-300">
                <div className="aspect-video overflow-hidden rounded-t-lg">
                  <img
                    src={project.images[0]?.url}
                    alt={project.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-lg">{project.title}</h3>
                      <div
                        className="text-sm text-muted-foreground line-clamp-2"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(project.description),
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{project.category}</Badge>
                      <Badge
                        variant={
                          project.status === "Completed"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {project.status}
                      </Badge>
                    </div>

                    <div className="flex justify-between items-center pt-2">
                      <div className="flex gap-2">
                        {project.technologies
                          ?.slice(0, 2)
                          .map((tech, techIndex) => (
                            <Badge
                              key={techIndex}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tech}
                            </Badge>
                          ))}
                        {project.technologies?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{project.technologies.length - 2}
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-blue-50 dark:hover:bg-blue-950/20"
                          onClick={() =>
                            navigate(`/admin/projects/edit/${project._id}`)
                          }
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-red-50 dark:hover:bg-red-950/20"
                          onClick={() =>
                            setDeleteModal({ isOpen: true, project })
                          }
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, project: null })}
        onConfirm={handleDelete}
        title="Delete Project"
        description="This will permanently remove the project from your portfolio."
        itemName={deleteModal.project?.title}
      />
    </motion.div>
  );
};

export default ProjectsManagement;
